import { useState, useEffect, ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  stocksAPI,
  ScreenTemplate,
  ScreenResponse,
  SavedScreenSummary,
  ScreenFilter,
  UniverseFilter,
} from '../../services/api';
import {
  ArrowLeft,
  TrendingUp,
  Filter,
  Plus,
  Play,
  Clock,
  Loader2,
  ChevronRight,
  BarChart3,
  Target,
  DollarSign,
  Zap,
  Shield,
  Users,
  X,
  Activity,
  Layers,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  History,
} from 'lucide-react';

// ============================================================================
// CONSTANTS
// ============================================================================

const CATEGORY_CONFIG: Record<string, { icon: ReactElement; color: string; bg: string }> = {
  value: {
    icon: <Target className="w-4 h-4" />,
    color: 'text-blue-600',
    bg: 'bg-blue-50 border-blue-200'
  },
  growth: {
    icon: <TrendingUp className="w-4 h-4" />,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50 border-emerald-200'
  },
  income: {
    icon: <DollarSign className="w-4 h-4" />,
    color: 'text-amber-600',
    bg: 'bg-amber-50 border-amber-200'
  },
  momentum: {
    icon: <Zap className="w-4 h-4" />,
    color: 'text-purple-600',
    bg: 'bg-purple-50 border-purple-200'
  },
  quality: {
    icon: <BarChart3 className="w-4 h-4" />,
    color: 'text-cyan-600',
    bg: 'bg-cyan-50 border-cyan-200'
  },
  defensive: {
    icon: <Shield className="w-4 h-4" />,
    color: 'text-gray-600',
    bg: 'bg-gray-50 border-gray-200'
  },
  sentiment: {
    icon: <Users className="w-4 h-4" />,
    color: 'text-pink-600',
    bg: 'bg-pink-50 border-pink-200'
  },
  macro: {
    icon: <Activity className="w-4 h-4" />,
    color: 'text-orange-600',
    bg: 'bg-orange-50 border-orange-200'
  },
  default: {
    icon: <BarChart3 className="w-4 h-4" />,
    color: 'text-gray-600',
    bg: 'bg-gray-50 border-gray-200'
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatMarketCap = (value: number | undefined): string => {
  if (!value) return '—';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  return `$${value.toLocaleString()}`;
};

const formatPercent = (value: number | undefined): string => {
  if (value === undefined || value === null) return '—';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

const formatTimeAgo = (dateStr: string | undefined): string => {
  if (!dateStr) return 'Never run';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ============================================================================
// TEMPLATE MINI CARD COMPONENT (for Quick Access)
// ============================================================================

interface TemplateMiniCardProps {
  template: ScreenTemplate;
  onRun: () => void;
  isLoading: boolean;
}

function TemplateMiniCard({ template, onRun, isLoading }: TemplateMiniCardProps): ReactElement {
  const config = CATEGORY_CONFIG[template.category] || CATEGORY_CONFIG.default;

  return (
    <button
      onClick={onRun}
      disabled={isLoading}
      className="group flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-white hover:border-gray-300 hover:shadow-sm transition-all text-left w-full"
    >
      <div className={`w-8 h-8 rounded-lg ${config.bg} border ${config.color} flex items-center justify-center flex-shrink-0`}>
        {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : config.icon}
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors block truncate">
          {template.name}
        </span>
        <span className="text-[10px] text-gray-500 font-mono">{template.filters.length} filters</span>
      </div>
      <Play className="w-3.5 h-3.5 text-gray-400 group-hover:text-emerald-500 transition-colors flex-shrink-0" />
    </button>
  );
}

// ============================================================================
// SAVED SCREEN ROW COMPONENT
// ============================================================================

interface SavedScreenRowProps {
  screen: SavedScreenSummary;
  onRun: () => void;
}

function SavedScreenRow({ screen, onRun }: SavedScreenRowProps): ReactElement {
  return (
    <div className="group flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-white hover:border-gray-300 hover:shadow-sm transition-all">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
          <Layers className="w-3.5 h-3.5 text-gray-500" />
        </div>
        <div className="min-w-0">
          <span className="text-sm text-gray-700 block truncate">{screen.name}</span>
          <div className="flex items-center gap-2 text-[10px] text-gray-500">
            <span className="font-mono">{screen.filter_count} filters</span>
            <span>·</span>
            <span>{formatTimeAgo(screen.last_run_at)}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={onRun}
          className="p-2 text-emerald-500 hover:bg-emerald-50 rounded-lg transition-colors"
        >
          <Play className="w-3.5 h-3.5" />
        </button>
        <Link
          to={`/stocks/screener?screen=${screen.screen_id}`}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

// ============================================================================
// QUICK RESULTS PANEL (kept from original)
// ============================================================================

interface QuickResultsPanelProps {
  title: string;
  results: ScreenResponse | null;
  loading: boolean;
  onClose: () => void;
  onPageChange: (offset: number) => void;
  onSort: (column: string, order: 'asc' | 'desc') => void;
  offset: number;
  limit: number;
  totalCount: number | null;
  filters: ScreenFilter[];
  universe: UniverseFilter | null;
  sortBy: string;
  sortOrder: string;
}

interface ColumnDef {
  key: string;
  label: string;
  sortKey: string;
  align: 'left' | 'right';
  sortable: boolean;
}

const RESULT_COLUMNS: ColumnDef[] = [
  { key: 'symbol', label: 'Symbol', sortKey: 'symbol', align: 'left', sortable: true },
  { key: 'company_name', label: 'Company', sortKey: 'company_name', align: 'left', sortable: true },
  { key: 'sector', label: 'Sector', sortKey: 'sector', align: 'left', sortable: true },
  { key: 'price', label: 'Price', sortKey: 'price', align: 'right', sortable: true },
  { key: 'price_change_pct', label: 'Chg%', sortKey: 'price_change_pct', align: 'right', sortable: true },
  { key: 'market_cap', label: 'Mkt Cap', sortKey: 'market_cap', align: 'right', sortable: true },
  { key: 'pe_ratio_ttm', label: 'P/E', sortKey: 'pe_ratio_ttm', align: 'right', sortable: true },
];

const formatFilterValue = (feature: string, value: number | string): string => {
  if (typeof value === 'string') return value;
  if (['roe', 'roic', 'gross_margin', 'net_profit_margin', 'dividend_yield'].includes(feature)) {
    return `${(value * 100).toFixed(1)}%`;
  }
  if (feature === 'market_cap') {
    if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(0)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
    return `$${value.toLocaleString()}`;
  }
  return value.toLocaleString();
};

const FEATURE_NAMES: Record<string, string> = {
  pe_ratio_ttm: 'P/E Ratio', pb_ratio: 'P/B Ratio', ev_to_ebitda: 'EV/EBITDA',
  peg_ratio: 'PEG Ratio', earnings_yield: 'Earnings Yield', fcf_yield: 'FCF Yield',
  roe: 'ROE', roic: 'ROIC', gross_margin: 'Gross Margin', net_profit_margin: 'Net Margin',
  dividend_yield: 'Div Yield', payout_ratio: 'Payout Ratio', debt_to_equity: 'Debt/Equity',
  current_ratio: 'Current Ratio', interest_coverage: 'Int. Coverage', beta: 'Beta',
  market_cap: 'Market Cap', price: 'Price', price_change_pct: 'Price Change %',
  high_52w: '52-Week High', low_52w: '52-Week Low', pct_from_high: '% From 52W High',
  pct_from_low: '% From 52W Low', return_1d: '1-Day Return', return_1w: '1-Week Return',
  return_1m: '1-Month Return', return_3m: '3-Month Return', return_6m: '6-Month Return',
  return_ytd: 'YTD Return', return_1y: '1-Year Return', volume: 'Volume',
  avg_volume: 'Avg Volume', relative_volume: 'Relative Volume', price_target_upside: 'PT Upside %',
};

function QuickResultsPanel({
  title, results, loading, onClose, onPageChange, onSort, offset, limit, totalCount,
  filters, universe, sortBy, sortOrder
}: QuickResultsPanelProps): ReactElement {
  const displayTotal = totalCount ?? results?.total_count ?? 0;
  const hasMore = results ? results.returned_count >= limit : false;
  const hasPrevious = offset > 0;

  const handleColumnClick = (column: ColumnDef) => {
    if (!column.sortable) return;
    const newOrder: 'asc' | 'desc' = sortBy === column.sortKey && sortOrder === 'desc' ? 'asc' : 'desc';
    onSort(column.sortKey, newOrder);
  };

  const getSortIcon = (column: ColumnDef) => {
    if (!column.sortable) return null;
    if (sortBy !== column.sortKey) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 text-emerald-400" /> : <ArrowDown className="w-3 h-3 text-emerald-400" />;
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col terminal-glow">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/80 bg-gradient-to-r from-slate-800/80 to-slate-900/80">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-amber-500/80" />
              <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
            </div>
            <div className="w-px h-5 bg-slate-700" />
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-emerald-400 terminal-pulse" />
              <h2 className="text-lg font-semibold text-slate-100" style={{ fontFamily: 'var(--font-display)' }}>{title}</h2>
            </div>
            {results && (
              <span className="px-2 py-1 text-[10px] text-slate-400 bg-slate-800 border border-slate-700 rounded font-mono">
                {results.execution_time_ms}ms
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {(filters.length > 0 || universe) && (
          <div className="px-6 py-3 border-b border-slate-700/50 bg-slate-800/30">
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Filter className="w-3.5 h-3.5" />
                <span className="font-medium uppercase tracking-wider">Active Filters</span>
              </div>
              <div className="flex flex-wrap gap-2 flex-1">
                {universe?.min_market_cap && (
                  <span className="px-2 py-1 text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded">
                    Market Cap ≥ {formatFilterValue('market_cap', universe.min_market_cap)}
                  </span>
                )}
                {universe?.max_market_cap && (
                  <span className="px-2 py-1 text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded">
                    Market Cap ≤ {formatFilterValue('market_cap', universe.max_market_cap)}
                  </span>
                )}
                {universe?.sectors && universe.sectors.length > 0 && (
                  <span className="px-2 py-1 text-xs bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded">
                    Sectors: {universe.sectors.join(', ')}
                  </span>
                )}
                {universe?.exclude_etfs && (
                  <span className="px-2 py-1 text-xs bg-slate-500/10 text-slate-400 border border-slate-500/20 rounded">No ETFs</span>
                )}
                {universe?.exclude_adrs && (
                  <span className="px-2 py-1 text-xs bg-slate-500/10 text-slate-400 border border-slate-500/20 rounded">No ADRs</span>
                )}
                {filters.map((f, idx) => (
                  <span key={idx} className="px-2 py-1 text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded font-mono">
                    {FEATURE_NAMES[f.feature] || f.feature} {f.operator} {formatFilterValue(f.feature, f.value as number)}
                  </span>
                ))}
                {sortBy && (
                  <span className="px-2 py-1 text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded">
                    Sort: {FEATURE_NAMES[sortBy] || sortBy} ({sortOrder})
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mb-4" />
              <p className="text-slate-400 text-sm">Executing screen...</p>
            </div>
          ) : results && results.results.length > 0 ? (
            <div className="data-table-terminal">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-800/95 backdrop-blur-sm border-b border-slate-700 z-10">
                  <tr>
                    {RESULT_COLUMNS.map((col) => (
                      <th key={col.key} className={`px-4 py-3.5 text-[10px] font-semibold uppercase tracking-wider ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                        <button
                          onClick={() => handleColumnClick(col)}
                          disabled={loading || !col.sortable}
                          className={`inline-flex items-center gap-1.5 transition-colors ${sortBy === col.sortKey ? 'text-emerald-400' : 'text-slate-400 hover:text-slate-200'} ${!col.sortable ? 'cursor-default' : 'cursor-pointer'}`}
                        >
                          {col.label}
                          {getSortIcon(col)}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {results.results.map((stock, idx) => (
                    <tr key={stock.symbol} className="hover:bg-slate-800/40 transition-colors" style={{ animationDelay: `${idx * 20}ms` }}>
                      <td className="px-4 py-3 font-mono font-semibold text-amber-400">
                        <Link to={`/stocks/${stock.symbol}`} className="hover:text-amber-300 transition-colors">{stock.symbol}</Link>
                      </td>
                      <td className="px-4 py-3 text-slate-300 truncate max-w-[200px]">{stock.company_name || '—'}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{stock.sector || '—'}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-200 tabular-nums">${stock.price?.toFixed(2) || '—'}</td>
                      <td className={`px-4 py-3 text-right font-mono font-medium tabular-nums ${(stock.price_change_pct || 0) >= 0 ? 'status-positive' : 'status-negative'}`}>
                        {formatPercent(stock.price_change_pct)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-300 tabular-nums">{formatMarketCap(stock.market_cap)}</td>
                      <td className="px-4 py-3 text-right font-mono text-slate-300 tabular-nums">{stock.pe_ratio_ttm?.toFixed(1) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <Filter className="w-12 h-12 mb-4 opacity-30" />
              <p>No stocks match the criteria</p>
            </div>
          )}
        </div>

        {results && (
          <div className="px-6 py-4 border-t border-slate-700 bg-slate-800/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => onPageChange(Math.max(0, offset - limit))}
                  disabled={!hasPrevious || loading}
                  className="px-4 py-2 text-sm text-slate-300 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-slate-400 font-mono">
                  {offset + 1} – {offset + results.returned_count}
                  {displayTotal > 0 && <span className="text-slate-500"> of {displayTotal.toLocaleString()}</span>}
                </span>
                <button
                  onClick={() => onPageChange(offset + limit)}
                  disabled={!hasMore || loading}
                  className="px-4 py-2 text-sm text-slate-300 bg-slate-800 border border-slate-700 rounded-lg hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
              <Link to="/stocks/screener" className="text-sm text-emerald-400 hover:text-emerald-300 font-medium flex items-center gap-1">
                Open in Screener
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function StrategiesHub(): ReactElement {
  const { isAuthenticated } = useAuth();

  // State
  const [templates, setTemplates] = useState<ScreenTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [savedScreens, setSavedScreens] = useState<SavedScreenSummary[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);

  // Quick results state
  const [runningTemplate, setRunningTemplate] = useState<string | null>(null);
  const [quickResults, setQuickResults] = useState<ScreenResponse | null>(null);
  const [quickResultsTitle, setQuickResultsTitle] = useState('');
  const [quickResultsLoading, setQuickResultsLoading] = useState(false);
  const [quickResultsOffset, setQuickResultsOffset] = useState(0);
  const [quickResultsTotalCount, setQuickResultsTotalCount] = useState<number | null>(null);
  const [currentTemplateKey, setCurrentTemplateKey] = useState<string | null>(null);
  const [currentSavedScreenId, setCurrentSavedScreenId] = useState<string | null>(null);
  const RESULTS_LIMIT = 100;

  const [currentFilters, setCurrentFilters] = useState<ScreenFilter[]>([]);
  const [currentUniverse, setCurrentUniverse] = useState<UniverseFilter | null>(null);
  const [currentSortBy, setCurrentSortBy] = useState<string>('');
  const [currentSortOrder, setCurrentSortOrder] = useState<string>('');

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setTemplatesLoading(true);
        const response = await stocksAPI.listTemplates();
        setTemplates(response.data);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      } finally {
        setTemplatesLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  // Fetch saved screens (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) {
      setSavedScreens([]);
      return;
    }
    const fetchSaved = async () => {
      try {
        setSavedLoading(true);
        const response = await stocksAPI.listSavedScreens();
        setSavedScreens(response.data);
      } catch (error) {
        console.error('Failed to fetch saved screens:', error);
      } finally {
        setSavedLoading(false);
      }
    };
    fetchSaved();
  }, [isAuthenticated]);

  // Run template handler
  const handleRunTemplate = async (
    template: ScreenTemplate,
    offset: number = 0,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc'
  ) => {
    setRunningTemplate(template.template_key);
    setQuickResultsLoading(true);
    setQuickResultsTitle(template.name);
    setCurrentTemplateKey(template.template_key);
    setCurrentSavedScreenId(null);

    const effectiveSortBy = sortBy || template.sort_by || '';
    const effectiveSortOrder = sortOrder || template.sort_order || 'desc';

    setCurrentFilters(template.filters);
    setCurrentUniverse(template.universe || null);
    setCurrentSortBy(effectiveSortBy);
    setCurrentSortOrder(effectiveSortOrder);

    if (offset === 0) {
      setQuickResults(null);
      setQuickResultsOffset(0);
      setQuickResultsTotalCount(null);
    }

    try {
      const includeCount = offset === 0;
      const customSortBy = sortBy !== undefined ? sortBy : undefined;
      const customSortOrder = sortOrder !== undefined ? sortOrder : undefined;
      const response = await stocksAPI.runTemplate(template.template_key, RESULTS_LIMIT, offset, includeCount, customSortBy, customSortOrder);
      setQuickResults(response.data);
      setQuickResultsOffset(offset);
      if (response.data.total_count > 0) {
        setQuickResultsTotalCount(response.data.total_count);
      }
    } catch (error) {
      console.error('Failed to run template:', error);
    } finally {
      setQuickResultsLoading(false);
      setRunningTemplate(null);
    }
  };

  // Run saved screen handler
  const handleRunSaved = async (screen: SavedScreenSummary, offset: number = 0) => {
    setQuickResultsLoading(true);
    setQuickResultsTitle(screen.name);
    setCurrentSavedScreenId(screen.screen_id);
    setCurrentTemplateKey(null);
    setCurrentFilters([]);
    setCurrentUniverse(null);
    setCurrentSortBy('');
    setCurrentSortOrder('');

    if (offset === 0) {
      setQuickResults(null);
      setQuickResultsOffset(0);
      setQuickResultsTotalCount(null);
    }

    try {
      const response = await stocksAPI.runSavedScreen(screen.screen_id, RESULTS_LIMIT, offset);
      setQuickResults(response.data);
      setQuickResultsOffset(offset);
      if (response.data.total_count > 0) {
        setQuickResultsTotalCount(response.data.total_count);
      }
    } catch (error) {
      console.error('Failed to run saved screen:', error);
    } finally {
      setQuickResultsLoading(false);
    }
  };

  const handleQuickResultsPageChange = (newOffset: number) => {
    if (currentTemplateKey) {
      const template = templates.find(t => t.template_key === currentTemplateKey);
      if (template) {
        handleRunTemplate(template, newOffset, currentSortBy || undefined, (currentSortOrder as 'asc' | 'desc') || undefined);
      }
    } else if (currentSavedScreenId) {
      const screen = savedScreens.find(s => s.screen_id === currentSavedScreenId);
      if (screen) handleRunSaved(screen, newOffset);
    }
  };

  const handleSort = (column: string, order: 'asc' | 'desc') => {
    if (currentTemplateKey) {
      const template = templates.find(t => t.template_key === currentTemplateKey);
      if (template) handleRunTemplate(template, 0, column, order);
    }
  };

  const closeQuickResults = () => {
    setQuickResults(null);
    setQuickResultsTitle('');
    setQuickResultsOffset(0);
    setQuickResultsTotalCount(null);
    setCurrentTemplateKey(null);
    setCurrentSavedScreenId(null);
    setCurrentFilters([]);
    setCurrentUniverse(null);
    setCurrentSortBy('');
    setCurrentSortOrder('');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header with back arrow */}
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/stocks"
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Strategies & Screens</h1>
            <p className="text-xs text-gray-500 mt-0.5">Flagship strategies, your saved screens, and recently viewed stocks</p>
          </div>
        </div>

        {/* ================================================================ */}
        {/* QUICK ACCESS SECTION */}
        {/* ================================================================ */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-6 rounded-full bg-gradient-to-b from-gray-400 to-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Quick Access</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Flagship Templates */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-emerald-600" />
                  <h4 className="text-sm font-semibold text-gray-900">Flagship Strategies</h4>
                  <span className="px-1.5 py-0.5 text-[9px] font-mono text-emerald-600 bg-emerald-50 rounded border border-emerald-200">{templates.length}</span>
                </div>
                <Link to="/stocks/screener" className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                  Custom Screen <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="p-4">
                {templatesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {templates.map((template) => (
                      <TemplateMiniCard
                        key={template.template_key}
                        template={template}
                        onRun={() => handleRunTemplate(template)}
                        isLoading={runningTemplate === template.template_key}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Recently Viewed + Saved Screens */}
            <div className="space-y-4">
              {/* Recently Viewed */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <History className="w-4 h-4 text-gray-500" />
                  <h4 className="text-sm font-semibold text-gray-900">Recently Viewed</h4>
                </div>
                <div className="p-4 text-center py-6 text-gray-500 text-sm">
                  <Clock className="w-6 h-6 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">No recent stocks</p>
                </div>
              </div>

              {/* Saved Screens */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-gray-500" />
                    <h4 className="text-sm font-semibold text-gray-900">My Screens</h4>
                    {savedScreens.length > 0 && (
                      <span className="px-1.5 py-0.5 text-[9px] font-mono text-gray-600 bg-gray-100 rounded border border-gray-200">{savedScreens.length}</span>
                    )}
                  </div>
                  <Link to="/stocks/screener" className="text-xs text-gray-500 hover:text-gray-700">
                    <Plus className="w-3 h-3" />
                  </Link>
                </div>
                <div className="p-3">
                  {savedLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
                    </div>
                  ) : savedScreens.length > 0 ? (
                    <div className="space-y-2">
                      {savedScreens.slice(0, 4).map((screen) => (
                        <SavedScreenRow key={screen.screen_id} screen={screen} onRun={() => handleRunSaved(screen)} />
                      ))}
                      {savedScreens.length > 4 && (
                        <div className="text-center pt-2">
                          <Link to="/stocks/screener" className="text-xs text-gray-500 hover:text-gray-700">
                            View all {savedScreens.length} screens
                          </Link>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-500">
                      <Layers className="w-6 h-6 mx-auto mb-2 opacity-30" />
                      <p className="text-xs">No saved screens</p>
                      <Link to="/stocks/screener" className="text-xs text-emerald-600 hover:text-emerald-700 mt-2 inline-block">
                        Create one
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Quick Results Panel */}
      {(quickResults || quickResultsLoading) && (
        <QuickResultsPanel
          title={quickResultsTitle}
          results={quickResults}
          loading={quickResultsLoading}
          onClose={closeQuickResults}
          onPageChange={handleQuickResultsPageChange}
          onSort={handleSort}
          offset={quickResultsOffset}
          limit={RESULTS_LIMIT}
          totalCount={quickResultsTotalCount}
          filters={currentFilters}
          universe={currentUniverse}
          sortBy={currentSortBy}
          sortOrder={currentSortOrder}
        />
      )}
    </div>
  );
}
